import { gql } from '@apollo/client';

export const CASE_STATUS_FIELDS = gql`
    fragment CaseStatusFields on Case {
        id
        clientId
        status
        workspaceV2Status
        clientInquiryStatus
        isEditable
        isApproved
        isBuiltInNext
        isClientInquiries
        canStartReport
        canSubmit
        canStartQc
        canSendback
        canApprove
        canClientInquiry
        canWriteFeedback
        canEditImprovementFeedback
        canPassFailFeedback
        canToggleBuiltInNext
        canUpdateReportScore
        canRetractApproval
        canEditCaseDetails
        canViewFeedbackPanel
        currentUserHasAccessToWebform
        canEditClickupLinks
        canEditUsernames
        canSearchTloData
        canAssignUsers
        canViewCaseOverviewPage
        canForceFailGenerateReport
        canBulkGenerateReport
        canUpdateCaseStatus
        activeQcId
        caseNotes
        unauthorizedErrors {
            canEditWebform
            resolveFeedback
        }
        caseHistory
    }
`;

export const CASE_NETWORK_INDEX_FIELDS = gql`
    fragment CaseNetworkIndexFields on CaseNetwork {
        id
        networkName
        category
        categoryId
        matchResult
        displayName
        ordinality
        link
        isBusiness
    }
`;

export const WORK_SESSION_FIELDS = gql`
    fragment WorkSessionFields on WorkSession {
        id
        startTime
        endTime
        durationInSeconds
        isActive
        formatStartDate
        formatStartTime
        formatEndTime
    }
`;

export const USER_PROFILE_FIELDS = gql`
    fragment UserProfile on User {
        id
        fullname
        displayName
        email
        nameInitials
        profileColor
        smallProfilePic
        userType
    }
`;

export const CONTRIBUTOR_FIELDS = gql`
    ${USER_PROFILE_FIELDS}
    fragment ContributorFields on Contributor {
        id
        accessType
        isAssignedAgent
        isAssignedQc
        isAssignedAqcm
        designation
        avatarRingColor
        user {
            ...UserProfile
        }
    }
`;

export const CASE_FIELDS = gql`
    ${CASE_STATUS_FIELDS}
    ${CASE_NETWORK_INDEX_FIELDS}
    ${USER_PROFILE_FIELDS}
    ${CONTRIBUTOR_FIELDS}
    fragment CaseFields on Case {
        ...CaseStatusFields
        id
        name
        profileId
        folderId
        useAlternateLayout
        formattedTrueDueDate
        formattedDueAt
        formattedAdjustedDueAt
        formattedOrderedAt
        currentProofingSessionIndex
        maxCaptureLimit
        claimNumber
        product {
            id
            name
        }
        pdfReportType
        agentId
        assignedQcId
        agent {
            ...UserProfile
        }
        assignedQc {
            ...UserProfile
        }
        assignedAqcm {
            ...UserProfile
        }
        assignedCsm {
            ...UserProfile
        }
        client {
            id
            name
            folderId
            customerSuccessManager
            sendMail
        }
        isUrgent
        isFirstOrder
        isOnHold
        isTraining
        isDataClientUpdated
        caseNetworks {
            ...CaseNetworkIndexFields
        }
        categories {
            id
            name
        }
        supportLinks
        instructionsSet
        isConverting
        isConvertSuccess
        isTest
        isFromKafka
        isGenerating
        score
        scoreRemarks
        orderId
        usernames
        chatsCount
        templateName
        caseSearchStrings {
            networkId
            networkName
            searchStrings
        }
        feedbacks {
            id
            verdict
            status
            section
            sectionId
            isUnresolved
            isResolved
        }
        contributors {
            ...ContributorFields
        }
        timelineSummary {
            remarks
            dateOrdered
            submitted {
                ...TimelineSummaryFields
            }
            failed {
                ...TimelineSummaryFields
            }
            approvals {
                ...TimelineSummaryFields
            }
            completed {
                ...TimelineSummaryFields
            }
        }
        isNewTemplate
        isProgressive
        isBuiltInNext
        currentUserApprovals {
            id
            isRetracted
            isSentBack
            isForReproof
        }
        isNewTemplate
        isProgressive
        isIq
        isFindingsEnabled
        isCiGeneral
        isCiRush
        profilePhoto {
            variantUrl
        }
        defaultPhoto
        clientHighlightRequest
        canOverrideTimeTracker
        feedbackCountSummary {
            edit {
                user {
                    ...UserProfile
                }
                count
            }
            improvement {
                user {
                    ...UserProfile
                }
                count
            }
        }
        confidenceScoreSummary {
            user {
                ...UserProfile
            }
            confidenceScore
        }
        webformSettings
        isManuallyAdjusted
        hasCiRequest
        isPilotOrder
    }
    fragment TimelineSummaryFields on TimelineSummaryItems {
        date
        createdBy
        ringColor
        approvalStatus
        proofingLayer
        user {
            ...UserProfile
        }
    }
`;

export const FEEDBACK_FIELDS = gql`
    fragment FeedbackFields on Feedback {
        id
        verdict
        status
        section
        sectionId
        fieldName
        messages {
            user
            body
            formattedCreatedAt
        }
        formattedCreatedAt
        isResolved
        isUnresolved
        user {
            id
            fullname
        }
        failures
        improvements
        isClientInquiry
        resolvedByUser {
            id
            fullname
        }
        proofingSessionIndex
    }
`;

export const FEEDBACK_FIELDS_V2 = gql`
    ${USER_PROFILE_FIELDS}
    fragment FeedbackFields on Feedback {
        id
        verdict
        status
        section
        sectionId
        sectionName
        fieldName
        content
        failures
        improvements
        sectionFieldKey
        proofingSessionIndex
        updatedAtTimestamp
        updatedAtUnixTimestamp
        formattedUpdatedAtDate
        formattedUpdatedAtTime
        user {
            ...UserProfile
        }
        resolvedByUser {
            ...UserProfile
        }
    }
`;

export const ATTACHMENT_FIELDS = gql`
    fragment AttachmentFields on ActiveStorageAttachment {
        filename
        url
        fileSize
        variantUrl
    }
`;

export const SCREENSHOT_FIELDS = gql`
    fragment ScreenshotFields on Screenshot {
        id
        imageFile {
            filename
            url
            fileSize
            verticalVariantUrl
            basename
        }
        externalLink
        startDate
        endDate
        reason
        metadata {
            filename
            url
        }
        videoFiles {
            filename
            url
            signedId
        }
        formatCreatedAt
        formatStartDate
        formatEndDate
        position
        isPinned
        description
        findingsCategories {
            id
            name
        }
    }
`;

export const CASE_NETWORK_FIELDS = gql`
    ${SCREENSHOT_FIELDS}
    ${ATTACHMENT_FIELDS}
    fragment CaseNetworkFields on CaseNetwork {
        id
        networkName
        isHighlightFound
        link
        profilePhoto {
            ...AttachmentFields
        }
        presence
        summary
        isBusiness
        isDirty
        matchResult
        displayName
        activityLevel
        activitySummary
        hasProfileLink
        hasProfilePhoto
        hasAttachments
        hasScreenshots
        hasFriendsFollowers
        hasActivitySummary
        findingsDocuments {
            id
            position
            document {
                filename
                url
            }
        }
        screenshots {
            ...ScreenshotFields
        }
        friendsFollowersLists{
            filename
            url
        }
    }
`;

export const USER_FIELDS = gql`
    fragment UserFields on User {
        id
        isDev
        isAdmin
        isSuperAdmin
        canReadSummary
        canGenerateStrippedMode
        isAgent
    }
`;

export const TEMPLATE_FIELDS = gql`
    fragment TemplateFields on TinymceTemplate {
        id
        title
        description
        content
    }
`;

export const CASE_DETAILS = gql`
    fragment CaseDetails on Case {
        id
        name
        productId
        product {
            id
            name
        }
        orderType
        searchDateStart
        formatSearchDateStart
        alias
        age
        dateOfBirth
        dateOfLoss
        formatDateOfBirth
        formatDateOfLoss
        gender
        templateName
        maxCaptureLimit
        clientDetails {
            emails
            phones {
                type
                phoneNumber
            }
            addresses {
                address
                city
                state
                zip
            }
            knownSocialUrls
            employments
            comprehensiveEmployments {
                employerType
                name
                position
                address
                startEndYear
            }
            schools
            educations {
                institution
                institutionType
                city
                degreeType
                yearGraduated
            }
            relationships
            ethnicity
            physicalDescriptions
            referralSource
            companyName
            ssn
        }
        claimDetails {
            claimId
            claimType
            dateOfInjury
            parseDateOfInjury
        }
        instructionsSet
        isClientInquiries
        isIq
        isProgressive
        canEditCaseDetails
        client {
            id
            hasReferralSource
            hasCompanyName
            isEmploymentsComprehensive
        }
        claimNumber
    }
`;

export const PROFILE_INFORMATION = gql`
    ${ATTACHMENT_FIELDS}
    fragment ProfileInformation on Case {
        id
        client {
            id
        }
        name
        emailAddress
        profilePhoto {
            ...AttachmentFields
        }
        findingsSummary
        mapViewPhoto {
            ...AttachmentFields
        }
        streetViewPhoto {
            ...AttachmentFields
        }
        relatives {
            id
            name
            relation
            link
            photo {
                ...AttachmentFields
            }
        }
        webformSettings
        isEditable
        isNewTemplate
        isIq
    }
`;

export const REPORT_SUMMARIES = gql`
    ${SCREENSHOT_FIELDS}
    fragment ReportSummaries on Case {
        id
        client {
            id
        }
        name
        isMatchFound
        findingsSummary
        findingsHighlight
        canPinScreenshots
        useAlternateLayout
        isIq
        isEditable
        matchType
        profileFindingsDocuments {
            id
            position
            document {
                filename
                url
            }
        }
        reportHighlightsScreenshots {
            ...ScreenshotFields
        }
    }
`;

export const CASE_CHAT_FIELDS = gql`
    ${USER_PROFILE_FIELDS}
    fragment CaseChatFields on CaseChat {
        id
        caseStatus
        content
        timestamp
        unixTimestamp
        parentCaseChat {
            id
            content
            attachments {
                id
            }
            author {
                id
                fullname
            }
            sectionName
            isDeleted
        }
        attachments {
            id
            filename
            url
            fileSize
        }
        author {
            ...UserProfile
        }
        section
        caseNetworkId
        sectionName
        isClientInquiry
        isDeleted
        formattedDeletedAt
        isAuthor
    }
`;

export const AGENT_LOG_FIELDS = gql`
    fragment AgentLogFields on AgentLog {
        id
        logText
        actionLabel
        caseStatus
        clientInquiryStatus
        timestamp
        unixTimestamp
    }
`;

export const PAGY_FIELDS = gql`
    fragment PagyFields on Pagy {
        count
        page
        items
        last
        pages
        offset
        from
        to
        in
        prev
        next
    }
`;